import { useEffect, useState } from 'react';
import { useAuth, useUser } from '@clerk/react';
import { Receipt, Sparkles, Wallet } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '@/shared/hooks';
import { useToast } from '@/shared/contexts/ToastContext';
import type { LeadFeedRow } from './leadFeedMapper';
import {
  MOCK_PAYMENT_METHODS,
  MOCK_PLAN_SUMMARY,
  MOCK_WALLET_BALANCE
} from './mockDashboardData';
import { ACCOUNT_ACCENT } from './accountStyles';
import { AccountSwitch } from './AccountSwitch';
import {
  AutoPurchaseHelpTrigger,
  type AutoPurchaseHelpTopic
} from './AutoPurchaseHelpDialogs';
import { AddCardModal } from './AddCardModal';
import type { WalletResponse } from './api/accountApi';
import {
  fetchAutoPurchaseSettings,
  fetchInventorySummary,
  saveAutoPurchaseSettings
} from './api/accountApi';
import { useDemoTopUp } from './hooks/useDemoTopUp';
import { usePaymentMethods } from './hooks/usePaymentMethods';
import { useStripeTopUp } from './hooks/useStripeTopUp';
import { useWallet } from './hooks/useWallet';
import { isStripePublishableConfigured } from './stripeBrowser';

function formatCardBrand(brand: string): string {
  if (!brand) return 'Card';
  return brand.slice(0, 1).toUpperCase() + brand.slice(1).toLowerCase();
}

const AccountDashboardPage = () => {
  useDocumentTitle('Dashboard - BrokerMax');
  const { notify } = useToast();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const { userId, sessionId, getToken } = useAuth();
  const qc = useQueryClient();
  const stripeUi = isStripePublishableConfigured();
  const {
    data: wallet,
    isLoading: walletLoading,
    isError: walletError
  } = useWallet();
  const {
    data: paymentMethods = [],
    isLoading: pmLoading,
    isError: pmError
  } = usePaymentMethods(stripeUi);
  const { data: inventorySummary, isLoading: inventoryLoading } = useQuery({
    queryKey: ['account', 'inventory-summary', userId, sessionId, getToken],
    queryFn: () => fetchInventorySummary(getToken),
    enabled: Boolean(userId)
  });
  const { data: autoPurchaseSettings, isLoading: autoPurchaseLoading } =
    useQuery({
      queryKey: ['account', 'autopurchase', userId, sessionId, getToken],
      queryFn: () => fetchAutoPurchaseSettings(getToken),
      enabled: Boolean(userId)
    });
  const saveAutoPurchase = useMutation({
    mutationFn: (settings: {
      buyer: boolean;
      renter: boolean;
      clearance: boolean;
    }) => saveAutoPurchaseSettings(getToken, settings),
    onSuccess: data => {
      setAutoBuyerEnabled(data.buyer);
      setAutoRenterEnabled(data.renter);
      setAutoClearanceEnabled(data.clearance);
      notify(
        'success',
        data.assignmentsCreated > 0
          ? `Autopurchase settings saved. Assigned ${data.assignmentsCreated} lead(s).`
          : 'Autopurchase settings saved. No eligible leads were purchased right now.'
      );
      void qc.invalidateQueries({ queryKey: ['account', 'autopurchase'] });
      void qc.invalidateQueries({ queryKey: ['account', 'wallet'] });
      void qc.invalidateQueries({ queryKey: ['account', 'leads'] });
    },
    onError: e => {
      notify(
        'warning',
        e instanceof Error ? e.message : 'Could not save settings'
      );
    }
  });

  const topUp = useDemoTopUp();
  const stripeTopUp = useStripeTopUp();

  const [demoPmId, setDemoPmId] = useState(MOCK_PAYMENT_METHODS[0]?.id ?? '');
  const [stripePmId, setStripePmId] = useState('');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [autoBuyerEnabled, setAutoBuyerEnabled] = useState(true);
  const [autoRenterEnabled, setAutoRenterEnabled] = useState(true);
  const [autoClearanceEnabled, setAutoClearanceEnabled] = useState(false);
  const [autoPurchaseHelpTopic, setAutoPurchaseHelpTopic] =
    useState<AutoPurchaseHelpTopic | null>(null);
  const [topUpHighlightOn, setTopUpHighlightOn] = useState(
    searchParams.get('focus') === 'topup'
  );

  useEffect(() => {
    if (!autoPurchaseSettings) return;
    setAutoBuyerEnabled(autoPurchaseSettings.buyer);
    setAutoRenterEnabled(autoPurchaseSettings.renter);
    setAutoClearanceEnabled(autoPurchaseSettings.clearance);
  }, [autoPurchaseSettings]);

  useEffect(() => {
    if (searchParams.get('focus') !== 'topup') return;
    setTopUpHighlightOn(true);
    const t = window.setTimeout(() => setTopUpHighlightOn(false), 5000);
    return () => window.clearTimeout(t);
  }, [searchParams]);

  useEffect(() => {
    if (!stripeUi || !paymentMethods.length) return;
    setStripePmId(prev =>
      prev && paymentMethods.some(p => p.id === prev)
        ? prev
        : paymentMethods[0]!.id
    );
  }, [stripeUi, paymentMethods]);

  const greetingName =
    user?.firstName ||
    user?.username ||
    user?.primaryEmailAddress?.emailAddress?.split('@')[0] ||
    'Agent';

  const balance = wallet?.balance ?? (walletError ? MOCK_WALLET_BALANCE : 0);
  const balanceLabel = `$${balance.toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

  const buyerPrice = wallet?.pricing.buyerLead ?? null;
  const rentalPrice = wallet?.pricing.rentalLead ?? null;
  const minLeadPrice =
    buyerPrice != null && rentalPrice != null
      ? Math.min(buyerPrice, rentalPrice)
      : null;
  const isBalanceTooLow =
    !walletLoading && minLeadPrice != null && balance < minLeadPrice;

  const profile = {
    email: user?.primaryEmailAddress?.emailAddress,
    name:
      user?.fullName ||
      [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
      undefined
  };

  const submitWalletTopUp = () => {
    const amount = parseFloat(topUpAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      notify('warning', 'Enter a valid amount in CAD.');
      return;
    }

    if (stripeUi) {
      if (!stripePmId) {
        notify('warning', 'Add a card first (Add card).');
        return;
      }
      const pm = paymentMethods.find(p => p.id === stripePmId);
      const cardLabel = pm
        ? `${formatCardBrand(pm.brand)} ···· ${pm.last4}`
        : 'selected card';
      const leadsKey = ['account', 'leads', userId, sessionId] as const;
      const leadsBefore = qc.getQueryData<LeadFeedRow[]>(leadsKey);

      stripeTopUp.mutate(
        { amountCad: amount, paymentMethodId: stripePmId },
        {
          onSuccess: () => {
            void (async () => {
              await qc.refetchQueries({ queryKey: ['account', 'wallet'] });
              await qc.refetchQueries({ queryKey: leadsKey });
              const w = qc.getQueryData<WalletResponse>([
                'account',
                'wallet',
                userId,
                sessionId
              ]);
              const leadsAfter = qc.getQueryData<LeadFeedRow[]>(leadsKey);
              const balanceStr = (w?.balance ?? 0).toLocaleString('en-CA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              let assigned = 0;
              if (leadsBefore != null && leadsAfter != null) {
                assigned = Math.max(0, leadsAfter.length - leadsBefore.length);
              }
              notify(
                'success',
                assigned > 0
                  ? `Charged $${amount.toFixed(2)} CAD (${cardLabel}). Assigned ${assigned} lead(s). Balance: $${balanceStr}.`
                  : `Charged $${amount.toFixed(2)} CAD (${cardLabel}). Balance: $${balanceStr}.`
              );
              setTopUpAmount('');
            })();
          },
          onError: e => {
            notify(
              'warning',
              e instanceof Error ? e.message : 'Payment failed'
            );
          }
        }
      );
      return;
    }

    const pm = MOCK_PAYMENT_METHODS.find(p => p.id === demoPmId);
    const cardLabel = pm ? `${pm.brand} ···· ${pm.last4}` : 'selected card';
    topUp.mutate(amount, {
      onSuccess: res => {
        notify(
          'success',
          `Added $${res.credited.toFixed(2)} CAD (${cardLabel}). Assigned ${res.assignmentsCreated} lead(s). Balance: $${res.balance.toFixed(2)}.`
        );
        if (res.assignmentsCreated === 0) {
          notify(
            'info',
            'No leads matched — set a territory that overlaps listings and ensure the API has eligible leads.'
          );
        }
        setTopUpAmount('');
      },
      onError: e => {
        notify('warning', e instanceof Error ? e.message : 'Top-up failed');
      }
    });
  };

  const topUpBusy = stripeUi
    ? stripeTopUp.isPending || pmLoading
    : topUp.isPending;
  const addFundsDisabled =
    topUpBusy ||
    walletLoading ||
    (stripeUi && (!paymentMethods.length || !stripePmId || pmError));

  const selectValue = stripeUi ? stripePmId : demoPmId;
  const selectOptions = stripeUi ? paymentMethods : MOCK_PAYMENT_METHODS;

  return (
    <div className="relative z-10">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
        Hello, {greetingName}
      </h1>
      <p className="mt-1 max-w-xl text-sm text-slate-700">
        Wallet, territory matching, and assigned leads
      </p>

      <div className="mt-5 rounded-2xl border border-slate-200/80 bg-white/85 p-4 shadow-sm backdrop-blur-sm md:p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#2b6cff]">
          Lead Quality Advantage
        </p>
        <p className="mt-2 inline-flex rounded-lg border border-[#2b6cff]/20 bg-[#eef4ff] px-3 py-1.5 text-sm font-bold text-[#1d4ed8]">
          Our leads come from real showing requests on Realtor.ca
        </p>
        <p className="mt-2 text-sm leading-relaxed text-slate-700 md:text-[15px]">
          This means higher intent and better conversion potential. Most lead
          brokers rely on ad traffic or forced form fills, which typically
          produces lower quality contacts. BrokerMax is built for
          premium-quality lead flow.
        </p>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:items-stretch">
        <div
          className={`relative overflow-hidden rounded-xl p-5 ${ACCOUNT_ACCENT.cardSurface} ${
            topUpHighlightOn
              ? 'ring-2 ring-[#2b6cff]/40 ring-offset-2 ring-offset-slate-100'
              : ''
          }`}
        >
          <Wallet
            className="pointer-events-none absolute right-4 top-4 h-5 w-5 text-slate-300"
            strokeWidth={1.5}
          />
          <p className="pr-8 text-sm font-medium text-slate-500">
            Wallet balance
          </p>
          <div className="mt-2 flex flex-wrap items-baseline gap-2">
            <span
              className={`text-2xl font-bold tabular-nums md:text-[1.75rem] ${
                isBalanceTooLow ? 'text-rose-700' : 'text-slate-900'
              }`}
            >
              {walletLoading ? '…' : balanceLabel}
            </span>
            <span className="text-sm font-normal text-slate-400">CAD</span>
          </div>
          {isBalanceTooLow ? (
            <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-700">
              Balance too low to purchase leads.
            </p>
          ) : null}
          {/* <p className="mt-2 text-xs text-slate-500">
            Capacity hint: up to{' '}
            <span className="font-semibold text-slate-700">{capBuyer}</span>{' '}
            buyer lead{capBuyer === 1 ? '' : 's'} or{' '}
            <span className="font-semibold text-slate-700">{capRental}</span>{' '}
            rental lead{capRental === 1 ? '' : 's'} at current prices (before
            the next match run).
          </p> */}

          <div className="my-4 border-t border-slate-100" aria-hidden />

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Top Up with Credit Card
            </p>
            {/* {stripeUi && pmError ? (
              <p className="text-xs text-amber-700">
                Could not load saved cards. Set{' '}
                <code className="rounded bg-slate-100 px-1">STRIPE_SECRET_KEY</code>{' '}
                on the API and restart{' '}
                <code className="rounded bg-slate-100 px-1">dev:api</code>.
              </p>
            ) : null} */}
            <label className="block text-xs font-medium text-slate-600">
              Pay with
              <select
                value={selectValue}
                onChange={e =>
                  stripeUi
                    ? setStripePmId(e.target.value)
                    : setDemoPmId(e.target.value)
                }
                disabled={stripeUi && (!paymentMethods.length || pmLoading)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none focus:ring-2 focus:ring-[#2b6cff]/25 disabled:opacity-60"
              >
                {selectOptions.length === 0 ? (
                  <option value="">No saved cards</option>
                ) : (
                  selectOptions.map(pm => (
                    <option key={pm.id} value={pm.id}>
                      {stripeUi
                        ? `${formatCardBrand(pm.brand)} ···· ${pm.last4}`
                        : `${pm.brand} ···· ${pm.last4}`}
                    </option>
                  ))
                )}
              </select>
            </label>
            {stripeUi ? (
              <button
                type="button"
                onClick={() => setAddCardOpen(true)}
                className="w-full rounded-lg border border-dashed border-[#2b6cff]/40 bg-sky-50/80 py-2 text-sm font-semibold text-[#2b6cff] hover:bg-sky-50"
              >
                Add card
              </button>
            ) : null}
            <label className="block text-xs font-medium text-slate-600">
              Amount
              <div className="mt-1.5 flex rounded-lg border border-slate-200 bg-white shadow-sm focus-within:ring-2 focus-within:ring-[#2b6cff]/25">
                <span className="flex items-center border-r border-slate-200 px-3 text-sm text-slate-500">
                  $
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={1}
                  placeholder="0"
                  value={topUpAmount}
                  onChange={e => setTopUpAmount(e.target.value)}
                  className="min-w-0 flex-1 rounded-r-lg px-3 py-2 text-sm tabular-nums text-slate-900 outline-none"
                />
                <span className="flex items-center border-l border-slate-200 px-3 text-xs font-medium text-slate-400">
                  CAD
                </span>
              </div>
            </label>
            <button
              type="button"
              onClick={submitWalletTopUp}
              disabled={addFundsDisabled}
              className="w-full rounded-lg bg-[#2b6cff] py-2.5 text-sm font-semibold text-white hover:bg-[#2563eb] disabled:opacity-50"
            >
              {topUpBusy ? 'Processing…' : 'Add funds'}
            </button>
          </div>
        </div>

        <div
          className={`relative flex flex-col overflow-hidden rounded-xl p-5 ${ACCOUNT_ACCENT.cardSurface}`}
        >
          <Receipt
            className="pointer-events-none absolute right-4 top-4 h-5 w-5 text-slate-300"
            strokeWidth={1.5}
          />
          <p className="text-sm font-medium text-slate-500">Plan & pricing</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <dt className="text-slate-500">Account type</dt>
              <dd className="font-semibold text-slate-900">
                {MOCK_PLAN_SUMMARY.accountType}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <dt className="text-slate-500">Plan type</dt>
              <dd className="font-semibold text-slate-900">
                {MOCK_PLAN_SUMMARY.planType}
              </dd>
            </div>
            <div
              className={`flex items-center justify-between gap-3 border-b border-slate-100 pb-3 ${
                isBalanceTooLow
                  ? 'rounded-md border border-rose-200 bg-rose-50 px-2 py-2'
                  : ''
              }`}
            >
              <dt className="text-slate-500">Buyer lead</dt>
              <dd
                className={`font-semibold tabular-nums ${
                  isBalanceTooLow ? 'text-rose-700' : 'text-slate-900'
                }`}
              >
                {buyerPrice != null
                  ? `$${buyerPrice.toLocaleString('en-US')}`
                  : '—'}
              </dd>
            </div>
            <div
              className={`flex items-center justify-between gap-3 ${
                isBalanceTooLow
                  ? 'rounded-md border border-rose-200 bg-rose-50 px-2 py-2'
                  : ''
              }`}
            >
              <dt className="text-slate-500">Tenant lead</dt>
              <dd
                className={`font-semibold tabular-nums ${
                  isBalanceTooLow ? 'text-rose-700' : 'text-slate-900'
                }`}
              >
                {rentalPrice != null
                  ? `$${rentalPrice.toLocaleString('en-US')}`
                  : '—'}
              </dd>
            </div>
          </dl>
          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span
                    id="autopurchase-label-buyer"
                    className="text-xs font-medium text-slate-700"
                  >
                    Auto-purchase buyer leads
                  </span>
                  <AutoPurchaseHelpTrigger
                    topic="buyer"
                    openTopic={autoPurchaseHelpTopic}
                    onOpenTopic={setAutoPurchaseHelpTopic}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[11px] font-bold tabular-nums ${
                      autoBuyerEnabled ? 'text-[#1d4ed8]' : 'text-slate-500'
                    }`}
                  >
                    {autoBuyerEnabled ? 'On' : 'Off'}
                  </span>
                  <AccountSwitch
                    checked={autoBuyerEnabled}
                    disabled={autoPurchaseLoading}
                    aria-labelledby="autopurchase-label-buyer"
                    onCheckedChange={setAutoBuyerEnabled}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span
                    id="autopurchase-label-renter"
                    className="text-xs font-medium text-slate-700"
                  >
                    Auto-purchase tenant leads
                  </span>
                  <AutoPurchaseHelpTrigger
                    topic="renter"
                    openTopic={autoPurchaseHelpTopic}
                    onOpenTopic={setAutoPurchaseHelpTopic}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[11px] font-bold tabular-nums ${
                      autoRenterEnabled ? 'text-[#1d4ed8]' : 'text-slate-500'
                    }`}
                  >
                    {autoRenterEnabled ? 'On' : 'Off'}
                  </span>
                  <AccountSwitch
                    checked={autoRenterEnabled}
                    disabled={autoPurchaseLoading}
                    aria-labelledby="autopurchase-label-renter"
                    onCheckedChange={setAutoRenterEnabled}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span
                    id="autopurchase-label-clearance"
                    className="text-xs font-medium text-slate-700"
                  >
                    Auto-purchase Clearance leads (50% off)
                  </span>
                  <AutoPurchaseHelpTrigger
                    topic="clearance"
                    openTopic={autoPurchaseHelpTopic}
                    onOpenTopic={setAutoPurchaseHelpTopic}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[11px] font-bold tabular-nums ${
                      autoClearanceEnabled ? 'text-[#1d4ed8]' : 'text-slate-500'
                    }`}
                  >
                    {autoClearanceEnabled ? 'On' : 'Off'}
                  </span>
                  <AccountSwitch
                    checked={autoClearanceEnabled}
                    disabled={autoPurchaseLoading}
                    aria-labelledby="autopurchase-label-clearance"
                    onCheckedChange={setAutoClearanceEnabled}
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              disabled={saveAutoPurchase.isPending || autoPurchaseLoading}
              onClick={() =>
                saveAutoPurchase.mutate({
                  buyer: autoBuyerEnabled,
                  renter: autoRenterEnabled,
                  clearance: autoClearanceEnabled
                })
              }
              className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {saveAutoPurchase.isPending
                ? 'Saving…'
                : 'Confirm autopurchase settings'}
            </button>
          </div>
        </div>

        <div
          className="relative flex min-h-0 flex-col overflow-hidden rounded-xl border border-indigo-400/30 p-4 text-white shadow-lg shadow-indigo-950/40 ring-1 ring-indigo-300/25 sm:col-span-2 sm:row-start-2 lg:col-span-1 lg:row-start-auto"
          style={{
            backgroundColor: '#071a3a',
            backgroundImage: [
              'radial-gradient(ellipse 85% 70% at 100% -5%, rgba(129,140,248,0.55), transparent 52%)',
              'radial-gradient(ellipse 60% 55% at -5% 100%, rgba(45,212,191,0.38), transparent 48%)',
              'linear-gradient(155deg, #042c5c 0%, #1557c4 42%, #4338ca 88%)'
            ].join(', ')
          }}
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
            }}
            aria-hidden
          />
          <div className="relative flex flex-1 flex-col">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/35 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white ring-1 ring-white/25">
                <Sparkles className="h-3 w-3 text-amber-200" strokeWidth={2} />
                BrokerMax
              </span>
            </div>
            <h3 className="mt-3 text-base font-bold leading-snug tracking-tight text-white drop-shadow-sm md:text-lg">
              Available inventory in your territory
            </h3>
            <p className="mt-1.5 text-xs leading-relaxed text-white/90">
              Counts in your saved territory (fresh vs clearance):
            </p>
            <div className="mt-3 flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-3 rounded-lg border border-white/40 bg-white px-3 py-2 shadow-sm">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-600">
                  Buyer Leads (Fresh)
                </p>
                <p className="text-lg font-bold tabular-nums text-slate-900">
                  {inventoryLoading ? '…' : (inventorySummary?.buyer ?? 0)}
                </p>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-white/40 bg-white px-3 py-2 shadow-sm">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-600">
                  Tenant Leads (Fresh)
                </p>
                <p className="text-lg font-bold tabular-nums text-slate-900">
                  {inventoryLoading ? '…' : (inventorySummary?.renter ?? 0)}
                </p>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-white/40 bg-white px-3 py-2 shadow-sm">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-600">
                  Clearance Leads
                </p>
                <p className="text-lg font-bold tabular-nums text-slate-900">
                  {inventoryLoading ? '…' : (inventorySummary?.clearance ?? 0)}
                </p>
              </div>
            </div>
            <div className="mt-auto pt-3">
              <p className="text-xs text-white/85">
                Updates automatically as inventory and assignments change.
              </p>
            </div>
          </div>
        </div>
      </div>

      {stripeUi ? (
        <AddCardModal
          open={addCardOpen}
          onOpenChange={setAddCardOpen}
          profile={profile}
          onCardSaved={paymentMethodId => {
            setStripePmId(paymentMethodId);
            notify('success', 'Card saved — you can top up now.');
          }}
        />
      ) : null}
    </div>
  );
};

export default AccountDashboardPage;
